import React, { useCallback, useMemo } from "react";
import {
  Autocomplete,
  Box,
  Checkbox,
  CircularProgress,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  Link,
  ListItemText,
  MenuItem,
  Select,
  Snackbar,
  SnackbarContent,
  Stack,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";

import {
  CalendarToday,
  Clear,
  LocationOn,
  SaveAlt,
  Search,
} from "@mui/icons-material";
import {
  SearchBox,
  StyledCalenderWrapper,
  TB,
  TableWrapper,
  Td,
  Th,
} from "./styles";
import useAminEvents from "./useAminEvents";
import DatePicker from "react-modern-calendar-datepicker";
import { styled } from "@mui/system";

export const CalenderIcon = styled(CalendarToday)`
  position: absolute;
  top: 5%;
  width: 20px;
  height: 22px;
  color: #306fbc;
  z-index: 98;
  margin-left: -32px;
  margin-top: 4px;
  cursor: pointer;
`;

const CONSTANTS = {
  COLORS: {
    PRIMARY: "#306fbc",
    PRIMARY_DARK: "#163A90",
    PRIMARY_LIGHT: "#91ABE9",
    SUCCESS: "#228b22",
    ERROR: "#CA3433",
    TEXT_PRIMARY: "#110641",
    WHITE: "#FFFFFF",
  },
  DIMENSIONS: {
    INPUT_HEIGHT: "35px",
    DATE_INPUT_WIDTH: "190px",
    SEARCH_WIDTH: "180px",
    SELECT_WIDTH: "250px",
    AUTOCOMPLETE_WIDTH: "200px",
    KEYWORD_TYPE_WIDTH: "180px",
    BORDER_RADIUS: "8px",
  },
  MENU: {
    ITEM_HEIGHT: 48,
    ITEM_PADDING_TOP: 8,
  },
};

const OPTIONS_TYPE = [
  { label: "Generic", value: "generic" },
  { label: "City based", value: "city_based" },
];

const formatDatePart = (datePart) => {
  if (!datePart) return null;
  const day = String(datePart.day).padStart(2, "0");
  const month = String(datePart.month).padStart(2, "0");
  return `${day}/${month}/${datePart.year}`;
};

const formatDate = (dateString) => {
  const date = new Date(dateString);
  const day = date.getDate().toString().padStart(2, "0");
  const month = (date.getMonth() + 1).toString().padStart(2, "0");
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

const CityAutocomplete = React.memo(
  ({ value, onChange, options, label, onPageReset }) => {
    const handleChange = useCallback(
      (e, newValue) => {
        onChange(newValue);
        onPageReset();
      },
      [onChange, onPageReset],
    );

    return (
      <Autocomplete
        multiple
        size="small"
        disableCloseOnSelect
        limitTags={2}
        options={options ?? []}
        getOptionLabel={(option) => option}
        value={value}
        onChange={handleChange}
        renderInput={(params) => (
          <TextField
            {...params}
            variant="outlined"
            label={label}
            size="small"
            InputProps={{
              ...params.InputProps,
              endAdornment: null,
            }}
            sx={{
              label: { background: "white" },
              ".MuiAutocomplete-inputRoot": {
                border: "1px solid rgba(0, 0, 0, 0.23)",
                width: CONSTANTS.DIMENSIONS.AUTOCOMPLETE_WIDTH,
                overflowX: "auto",
                flexWrap: "nowrap",
                borderRadius: CONSTANTS.DIMENSIONS.BORDER_RADIUS,
                "&::-webkit-scrollbar": { display: "none" },
                "-ms-overflow-style": "none",
                "scrollbar-width": "none",
                input: { minWidth: 100 },
                fieldset: { display: "none" },
              },
              "& .MuiAutocomplete-inputRoot.Mui-focused": {
                border: "2px solid #1976d2",
              },
            }}
          />
        )}
        renderOption={(props, option, { selected }) => (
          <li {...props}>
            <Checkbox style={{ marginRight: 8 }} checked={selected} />
            <ListItemText primary={option} />
          </li>
        )}
      />
    );
  },
);

const EventAddressCell = React.memo(({ event }) => {
  const hasValidCoordinates =
    event?.latitude !== "None" && event?.longitude !== "None";

  return (
    <Box
      display="flex"
      justifyContent={event.address ? "start" : "center"}
      width="100%"
      gap={1}
    >
      {hasValidCoordinates && (
        <Link
          href={`https://www.google.com/maps/search/?api=1&query=${event.latitude}%2C${event.longitude}`}
          target="_blank"
          rel="noreferrer"
          aria-label="View location on Google Maps"
        >
          <LocationOn sx={{ color: CONSTANTS.COLORS.PRIMARY_DARK }} />
        </Link>
      )}
      <Typography textAlign={event.address ? "left" : "center"}>
        {event.address || "N/A"}
      </Typography>
    </Box>
  );
});

export default function AdminEvents() {
  const {
    dateNew,
    setDateNew,
    setPage,
    openSearch,
    setOpenSearch,
    searchInput,
    setSearchInput,
    eventDetails,
    page,
    count,
    rowsPerPage,
    handleChangePage,
    handleChangeRowsPerPage,
    networkMsg,
    setnetworkMsg,
    selectedType,
    setSelectedType,
    eventTypes,
    type,
    setType,
    options,
    selectedKeywords,
    setSelectedKeywords,
    setSearchKeyword,
    cityList,
    city,
    setCity,
    loading,
    downloadFile,
    cityAll,
    cityValue,
    setCityValue,
  } = useAminEvents();

  const formatInputValue = useMemo(() => {
    if (!dateNew?.from) return "";
    const from = formatDatePart(dateNew.from);
    const to = dateNew.to ? formatDatePart(dateNew.to) : "End Date";
    return `${from} - ${to}`;
  }, [dateNew]);

  const renderCustomInput = useCallback(
    ({ ref }) => (
      <input
        readOnly
        ref={ref}
        value={formatInputValue}
        aria-label="Date range picker"
        style={{
          textAlign: "left",
          marginBottom: "2px",
          fontSize: "12px",
          borderRadius: CONSTANTS.DIMENSIONS.BORDER_RADIUS,
          color: "#000000",
          outline: "none",
          height: CONSTANTS.DIMENSIONS.INPUT_HEIGHT,
          border: "none",
          fontWeight: 500,
          font: "500 12px / 14px Roboto",
          cursor: "pointer",
          width: CONSTANTS.DIMENSIONS.DATE_INPUT_WIDTH,
          paddingLeft: "10px",
        }}
        className="my-custom-input-class"
      />
    ),
    [formatInputValue],
  );

  const handlePageReset = useCallback(() => setPage(0), [setPage]);

  const handleType = useCallback(
    (event) => {
      const { value } = event.target;
      setSelectedType(typeof value === "string" ? value.split(",") : value);
      handlePageReset();
    },
    [setSelectedType, handlePageReset],
  );

  /* const handleCity = useCallback(
    (event) => {
      const { value } = event.target;
      setCity(typeof value === "string" ? value.split(",") : value);
      handlePageReset();
    },
    [setCity, handlePageReset],
  );*/

  const handleDateChange = useCallback(
    (date) => {
      setDateNew(date);
      handlePageReset();
    },
    [setDateNew, handlePageReset],
  );

  const handleSearchChange = useCallback(
    (e) => {
      setSearchInput(e?.target?.value ?? "");
      handlePageReset();
    },
    [setSearchInput, handlePageReset],
  );

  const clearSearch = useCallback(() => {
    setSearchInput("");
  }, [setSearchInput]);

  const toggleSearch = useCallback(() => {
    setOpenSearch((prev) => !prev);
  }, [setOpenSearch]);

  const handleTypeChange = useCallback(
    (e) => {
      setType(e?.target?.value ?? "");
      setSelectedKeywords([]);
      handlePageReset();
    },
    [setType, setSelectedKeywords, handlePageReset],
  );

  const handleKeywordsChange = useCallback(
    (e, value) => {
      setSelectedKeywords(value);
      handlePageReset();
    },
    [setSelectedKeywords, handlePageReset],
  );

  const handleSnackbarClose = useCallback(() => {
    setnetworkMsg(false);
  }, [setnetworkMsg]);

  const eventTypeSelectMenuProps = useMemo(
    () => ({
      PaperProps: {
        style: {
          width: CONSTANTS.DIMENSIONS.SELECT_WIDTH,
          maxHeight: "240px",
          overflowY: "auto",
        },
      },
      anchorOrigin: {
        vertical: "bottom",
        horizontal: "left",
      },
      transformOrigin: {
        vertical: "top",
        horizontal: "left",
      },
      getContentAnchorEl: null,
    }),
    [],
  );

  return (
    <Box
      sx={{
        padding: "10px 10px 10px 10px",
        height: "79vh",
        backgroundColor: CONSTANTS.COLORS.WHITE,
      }}
    >
      <Stack mt={1} direction="row" width="100%" justifyContent="end">
        <Stack direction="row" gap={2}>
          <Tooltip arrow placement="top" title="Select Date Range">
            <StyledCalenderWrapper>
              <DatePicker
                value={dateNew}
                onChange={handleDateChange}
                colorPrimaryLight={CONSTANTS.COLORS.PRIMARY_LIGHT}
                colorPrimary={CONSTANTS.COLORS.PRIMARY_DARK}
                calendarPopperPosition="bottom"
                calendarClassName="myCustomCalendar"
                renderInput={renderCustomInput}
              />
              <CalenderIcon />
            </StyledCalenderWrapper>
          </Tooltip>

          <Stack
            sx={{
              boxShadow: `0px 0px 4px rgba(48, 111, 188, 0.2)`,
              borderRadius: CONSTANTS.DIMENSIONS.BORDER_RADIUS,
              overflow: "hidden",
            }}
            direction="row"
          >
            {openSearch && (
              <TextField
                placeholder="search"
                variant="outlined"
                value={searchInput}
                onChange={handleSearchChange}
                InputProps={{
                  endAdornment: searchInput ? (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={clearSearch}
                        edge="end"
                        aria-label="Clear search"
                      >
                        <Clear />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                }}
                sx={{
                  width: openSearch ? CONSTANTS.DIMENSIONS.SEARCH_WIDTH : "0px",
                  opacity: openSearch ? 1 : 0,
                  transition: "width 0.4s ease, opacity 0.3s ease",
                  "& .MuiInputBase-root": {
                    borderRadius: CONSTANTS.DIMENSIONS.BORDER_RADIUS,
                    height: CONSTANTS.DIMENSIONS.INPUT_HEIGHT,
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    border: `1px solid ${CONSTANTS.COLORS.WHITE} !important`,
                    "&:focus": {
                      border: `1px solid ${CONSTANTS.COLORS.WHITE} !important`,
                      outline: "none",
                    },
                  },
                  "& .Mui-focused .MuiOutlinedInput-notchedOutline": {
                    border: `1px solid ${CONSTANTS.COLORS.WHITE} !important`,
                  },
                }}
              />
            )}
            <SearchBox onClick={toggleSearch} aria-label="Toggle search">
              <Search />
            </SearchBox>
          </Stack>

          <IconButton
            onClick={downloadFile}
            aria-label="Download events"
            sx={{
              padding: "0 8px",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              backgroundColor: CONSTANTS.COLORS.WHITE,
              borderRadius: CONSTANTS.DIMENSIONS.BORDER_RADIUS,
              color: CONSTANTS.COLORS.PRIMARY,
              boxShadow: "0px 0px 4px rgba(48, 111, 188, 0.2)",
              height: CONSTANTS.DIMENSIONS.INPUT_HEIGHT,
              "&:hover": {
                cursor: "pointer",
              },
            }}
          >
            <SaveAlt />
          </IconButton>
        </Stack>
      </Stack>

      <Stack
        direction="row"
        justifyContent="start"
        alignItems="center"
        mt={1}
        gap={1}
      >
        <FormControl
          variant="outlined"
          sx={{
            width: CONSTANTS.DIMENSIONS.SELECT_WIDTH,
            borderRadius: CONSTANTS.DIMENSIONS.BORDER_RADIUS,
            "& .MuiOutlinedInput-root": {
              borderRadius: CONSTANTS.DIMENSIONS.BORDER_RADIUS,
            },
          }}
          size="small"
        >
          <InputLabel id="event-category-label">Event Category</InputLabel>
          <Select
            multiple
            value={selectedType}
            onChange={handleType}
            disableUnderline
            label="Event Category"
            renderValue={(selected) =>
              selected
                .map((id) => eventTypes.find((type) => type?.id === id)?.name)
                .join(", ")
            }
            MenuProps={eventTypeSelectMenuProps}
            style={{ color: CONSTANTS.COLORS.TEXT_PRIMARY }}
          >
            {eventTypes.map((name) => (
              <MenuItem key={name?.id} value={name?.id}>
                <Checkbox checked={selectedType.includes(name?.id)} />
                <ListItemText
                  sx={{ whiteSpace: "normal" }}
                  primary={name?.name}
                />
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <CityAutocomplete
          value={cityValue}
          onChange={setCityValue}
          options={cityAll}
          label="Event City"
          onPageReset={handlePageReset}
        />

        <TextField
          select
          label="Keyword Type"
          size="small"
          value={type}
          onChange={handleTypeChange}
          sx={{
            width: CONSTANTS.DIMENSIONS.KEYWORD_TYPE_WIDTH,
            "& .MuiInputBase-root": {
              borderRadius: CONSTANTS.DIMENSIONS.BORDER_RADIUS,
            },
          }}
        >
          {OPTIONS_TYPE.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>

        {type === "city_based" && (
          <CityAutocomplete
            value={city}
            onChange={setCity}
            options={cityList}
            label="City"
            onPageReset={handlePageReset}
          />
        )}

        <Autocomplete
          multiple
          size="small"
          limitTags={2}
          options={options ?? []}
          getOptionLabel={(option) => option.keywordName}
          value={selectedKeywords}
          onChange={handleKeywordsChange}
          onInputChange={(e, newInputValue) => setSearchKeyword(newInputValue)}
          renderInput={(params) => (
            <TextField
              {...params}
              variant="outlined"
              label="Keywords"
              size="small"
              InputProps={{
                ...params.InputProps,
                endAdornment: null,
              }}
              sx={{
                label: { background: "white" },
                ".MuiAutocomplete-inputRoot": {
                  border: "1px solid rgba(0, 0, 0, 0.23)",
                  width: CONSTANTS.DIMENSIONS.AUTOCOMPLETE_WIDTH,
                  overflowX: "auto",
                  flexWrap: "nowrap",
                  borderRadius: CONSTANTS.DIMENSIONS.BORDER_RADIUS,
                  "&::-webkit-scrollbar": { display: "none" },
                  "-ms-overflow-style": "none",
                  "scrollbar-width": "none",
                  input: { minWidth: 100 },
                  fieldset: { display: "none" },
                },
                "& .MuiAutocomplete-inputRoot.Mui-focused": {
                  border: "2px solid #1976d2",
                },
              }}
            />
          )}
        />
      </Stack>

      {loading ? (
        <Box
          sx={{
            height: "100%",
            width: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <Box>
          <TableWrapper>
            <TB
              stickyHeader
              aria-label="events table"
              style={{
                borderRadius: `${CONSTANTS.DIMENSIONS.BORDER_RADIUS} ${CONSTANTS.DIMENSIONS.BORDER_RADIUS} 0px 0px`,
              }}
            >
              <TableHead>
                <TableRow>
                  <Th style={{ borderRadius: "8px 0px 0px 0px" }}>
                    Event Name
                  </Th>
                  <Th>Event Category</Th>
                  <Th>Event Date</Th>
                  <Th>Event Venue</Th>
                  <Th>Event Address</Th>
                  <Th>Number of Attendees</Th>
                  <Th style={{ borderRadius: "0px 8px 0px 0px" }}>
                    Event City
                  </Th>
                </TableRow>
              </TableHead>
              <TableBody>
                {eventDetails?.events?.length === 0 && (
                  <TableRow>
                    <TableCell
                      colSpan={7}
                      sx={{ textAlign: "center", border: "0px" }}
                    >
                      <Box
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        width="100%"
                        height="200px"
                      >
                        <Typography>No Events Found</Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
                {eventDetails?.events?.map((event) => (
                  <TableRow key={event.eventID}>
                    <Td
                      sx={{ maxWidth: "150px", textAlign: "left !important" }}
                    >
                      {event?.name}
                    </Td>
                    <Td sx={{ maxWidth: "120px" }}>{event.category}</Td>
                    <Td sx={{whiteSpace: "normal", maxWidth: "130px" }}>
                      {event?.isGrouped
                        ? `${formatDate(event.startDate)} - ${formatDate(
                            event.endDate,
                          )}`
                        : formatDate(event.startDate)}
                    </Td>
                    <Td sx={{ maxWidth: "100px" }}>{event?.venue}</Td>
                    <Td sx={{ maxWidth: "170px", textAlign: "left" }}>
                      <EventAddressCell event={event} />
                    </Td>
                    <Td>{event?.estimatedAttendees ?? "N/A"}</Td>
                    <Td>{event?.city ?? "N/A"}</Td>
                  </TableRow>
                ))}
              </TableBody>
            </TB>
          </TableWrapper>
          <TablePagination
            rowsPerPageOptions={[10, 25, 50]}
            component="div"
            count={count}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Box>
      )}

      {/* Snackbar */}
      <Snackbar
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        open={!!networkMsg}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
      >
        {networkMsg && (
          <SnackbarContent
            style={{
              backgroundColor:
                networkMsg === "Event Added Successfully"
                  ? CONSTANTS.COLORS.SUCCESS
                  : CONSTANTS.COLORS.ERROR,
            }}
            message={networkMsg}
          />
        )}
      </Snackbar>
    </Box>
  );
}
